import {SVGOptions} from "../configuration/style";
import {SVGCallback} from "../general/callback";
import {GeometryShape, Icon, Symbol} from "../configuration/layer";

export class SVG {

    private readonly namespace: string;
    private readonly textWidth: number;
    private readonly textVertical: number;
    private readonly stepVertical: number;
    private readonly elementWidth: number;
    private readonly elementHeight: number;

    constructor() {
        this.namespace = "http://www.w3.org/2000/svg";
        this.textWidth = 38;
        this.textVertical = 15;
        this.stepVertical = 20;
        this.elementWidth = 20;
        this.elementHeight = 15;
    }

    hatches (id: string, width: number | undefined, size: number | undefined, rotate: number | undefined, color: string): SVGPatternElement {
        const pattern = document.createElementNS(this.namespace,"pattern") as SVGPatternElement;
        pattern.setAttribute('id',id);
        pattern.setAttribute('patternUnits','userSpaceOnUse');
        const sizeAttribute: string = size ? size.toString() : '';
        pattern.setAttribute('width',sizeAttribute);
        pattern.setAttribute('height','10');
        const rotateAtribute = rotate ? 'rotate(' + rotate.toString() + ' 0 0)' : '';
        pattern.setAttribute('patternTransform',rotateAtribute);

        const line = document.createElementNS(this.namespace,"line");
        line.setAttribute('x1','0');
        line.setAttribute('y1','0');
        line.setAttribute('x2','0');
        line.setAttribute('y2','10');
        const strokeWidth: string = width ? 'stroke-width:' + width.toString() : '';
        line.setAttribute('style', 'stroke:' + color + '; ' + strokeWidth);

        pattern.appendChild(line);

        return pattern;
    }

    continuous(steps: any[], callback: SVGCallback | undefined, options: SVGOptions | undefined): HTMLElement | SVGElement | string {

        const svgElement = document.createElementNS(this.namespace, "svg") as SVGElement;

        const defs = document.createElementNS(this.namespace,"defs");
        const linearGradient = document.createElementNS(this.namespace,"linearGradient");
        linearGradient.setAttribute('id','gradient');
        linearGradient.setAttribute('gradientTransform','rotate(90)');
        defs.appendChild(linearGradient);

        let textY = this.textVertical;
        let stepHeight = this.stepVertical;

        steps.map(style => {
            const stop = document.createElementNS(this.namespace,"stop");
            stop.setAttribute("offset", style.value + "%");
            stop.setAttribute("style", "stop-color:" + style.fill.color)

            const text = document.createElementNS(this.namespace,"text");
            text.setAttribute("x", String(this.textWidth));
            text.setAttribute("y",String(textY));
            text.setAttribute("class", "ramp-label");
            text.textContent = style.title;

            textY = textY + stepHeight;

            linearGradient.appendChild(stop);
            svgElement.appendChild(text);
        })

        svgElement.setAttribute('height', String(textY - stepHeight));

        svgElement.appendChild(defs);

        const element = this.rect(8,10,25,textY - stepHeight - 15);
        element.setAttribute("fill",'url(#gradient)');

        svgElement.appendChild(element);

        let propertyIcon;

        if (options && options.mode === 'svg') {
            propertyIcon = svgElement;
        } else {
            //Default 'web' version
            propertyIcon = document.createElement('i');
            propertyIcon.className = 'cfm-layer-entry-icon-ramp';

            propertyIcon.appendChild(svgElement);
        }

        if(options && options.format === 'outerHTML') {
            propertyIcon = propertyIcon.outerHTML;
        }

        if (callback) {
            callback(propertyIcon);
        }

        return propertyIcon;
    }

    shape(symbol: any, width: number, height: number): SVGElement | DocumentFragment | undefined {
        let shape: SVGElement;
        //Shape is around svg's center
        const centerX = Math.round(width/2);
        const centerY = Math.round(height/2);
        let A,B,C,D,E;

        switch (symbol.shape) {
            case 'line':
                shape = document.createElementNS(this.namespace, "path") as SVGElement;
                const startX = centerX - symbol.size * 2;
                const startY = centerY;
                const control1X = centerX - symbol.size;
                const control1Y = centerY - symbol.size;
                const midX = centerX;
                const midY = centerY;
                const control2X = centerX + symbol.size;
                const control2Y = centerY + symbol.size;
                const endX = centerX + symbol.size * 2;
                const endY = centerY;

                const d = `M ${startX} ${startY} Q ${control1X} ${control1Y}, ${midX} ${midY} Q ${control2X} ${control2Y}, ${endX} ${endY}`;

                shape.setAttribute("d", d);
                shape.setAttribute("fill", "none");
                shape.setAttribute("stroke", symbol.fill ? symbol.fill.color : symbol.stroke.color);
                shape.setAttribute("stroke-width", (symbol.stroke.width*2).toString());

                // Add a thicker wavy line for the fill effect
                const fillShape = document.createElementNS(this.namespace, "path");
                fillShape.setAttribute("d", d);
                fillShape.setAttribute("fill", "none");
                fillShape.setAttribute("stroke", symbol.stroke.color);
                fillShape.setAttribute("stroke-width", (symbol.stroke.width*4).toString());

                // Create a document fragment to hold both shapes
                const fragment: DocumentFragment = document.createDocumentFragment();
                fragment.appendChild(fillShape);
                fragment.appendChild(shape);

                return fragment;
            case 'triangle':
                shape = document.createElementNS(this.namespace, "polygon") as SVGElement;
                A = (centerX - symbol.size).toString() + " " + (centerY + symbol.size).toString();
                B = (centerX).toString() + " " + (centerY - symbol.size).toString();
                C = (centerX + symbol.size).toString() + " " + (centerY + symbol.size).toString();

                shape.setAttribute("points", A + " " + B + " " + C + " " + A);
                shape.setAttribute("fill", symbol.fill.color);
                shape.setAttribute("stroke", symbol.stroke.color);
                shape.setAttribute("stroke-width",symbol.stroke.width);
                return shape;
            case 'circle':
                shape = document.createElementNS(this.namespace, "circle") as SVGElement;

                shape.setAttribute("cx", centerX.toString());
                shape.setAttribute("cy", centerY.toString());
                shape.setAttribute("r", symbol.size.toString());
                shape.setAttribute("fill", symbol.fill.color);
                shape.setAttribute("stroke", symbol.stroke.color);
                shape.setAttribute("stroke-width",symbol.stroke.width);
                return shape;
            case 'box':
                shape = document.createElementNS(this.namespace, "polygon") as SVGElement;
                const sizePath = symbol.size/2;
                A = (centerX - sizePath).toString() + " " + (centerY + sizePath).toString();
                B = (centerX + sizePath).toString() + " " + (centerY + sizePath).toString();
                C = (centerX + sizePath).toString() + " " + (centerY - sizePath).toString();
                D = (centerX - sizePath).toString() + " " + (centerY - sizePath).toString();
                E = (centerX - sizePath).toString() + " " + (centerY + sizePath).toString();

                shape.setAttribute("points", A + " " + B + " " + C + " " + D + " " + E);
                shape.setAttribute("fill", symbol.fill.color);
                shape.setAttribute("stroke", symbol.stroke.color);
                shape.setAttribute("stroke-width",symbol.stroke.width);
                return shape;
        }
    }

    rect(x: number, y: number, width: number, height: number): SVGRectElement {
        const rect = document.createElementNS(this.namespace,"rect") as SVGRectElement;
        rect.setAttribute("x",String(x));
        rect.setAttribute("y",String(y));
        rect.setAttribute("width",String(width));
        rect.setAttribute("height",String(height));

        return rect;
    }

    uid(): string {
        return String.fromCharCode(Math.floor(Math.random() * 26) + 97)
            + Math.random().toString(16).slice(2)
            + Date.now().toString(16).slice(4);
    }

    unique(geometry: GeometryShape, symbol: Icon & Symbol, callback: SVGCallback | undefined, options: SVGOptions | undefined): string | SVGElement | HTMLElement {

        const defs = document.createElementNS(this.namespace,"defs");
        const svgElement = document.createElementNS(this.namespace, "svg") as SVGElement;
        svgElement.setAttribute('height', String(this.elementHeight + 8));
        svgElement.setAttribute('width', String(this.elementWidth + 4));
        svgElement.appendChild(defs);

        let element: SVGElement | DocumentFragment | undefined;

        switch (geometry) {
            case 'point':
                element = this.shape(symbol, this.elementWidth, this.elementHeight);
                if (element) {
                    svgElement.appendChild(element);
                }
                break;
            case 'line':
                element = this.shape(symbol, this.elementWidth, this.elementHeight);
                if (element) {
                    svgElement.appendChild(element);
                }
                break;
            case 'polygon':
                element = this.rect(2,2, this.elementWidth, this.elementHeight);

                if (symbol.fill) {
                    if (symbol.fill.type === 'line-pattern') {
                        const patternId = this.uid();
                        const pattern = this.hatches(patternId,symbol.fill.width,symbol.fill.size, symbol.fill.rotation, symbol.fill.color);
                        defs.appendChild(pattern);
                        element.setAttributeNS(null,"fill", 'url(#' + patternId + ')');
                    } else if (symbol.fill.type === 'perpendicular-pattern') {
                        const firstPatternId = this.uid();
                        const pattern = this.hatches(firstPatternId,symbol.fill.width,symbol.fill.size, symbol.fill.rotation, symbol.fill.color);
                        defs.appendChild(pattern);
                        element.setAttributeNS(null,"fill", 'url(#' + firstPatternId + ')');

                        const secondElement = this.rect(2,2,this.elementWidth,this.elementHeight);

                        const secondPatternId = this.uid();
                        const rotationAtribute = symbol.fill.rotation ? symbol.fill.rotation - 90 : 0;
                        const secondPattern = this.hatches(secondPatternId,symbol.fill.width,symbol.fill.size, rotationAtribute, symbol.fill.color);
                        defs.appendChild(secondPattern);
                        svgElement.appendChild(secondElement);
                        secondElement.setAttributeNS(null,"fill", 'url(#' + secondPatternId + ')');
                    } else {
                        element.setAttributeNS(null,"fill", symbol.fill.color);
                    }
                } else {
                    element.setAttributeNS(null,"fill-opacity", '0');
                    element.setAttributeNS(null,"fill", "transparent");
                }
                if (symbol.stroke) {
                    element.setAttributeNS(null,"stroke",symbol.stroke.color);
                    element.setAttributeNS(null,"stroke-width", String(symbol.stroke.width));
                    if(symbol.stroke.dasharray) {
                        element.setAttributeNS(null,"stroke-dasharray",symbol.stroke.dasharray);
                    }
                }

                svgElement.appendChild(element);

                break;
        }

        let propertyIcon;

        if (options && options.mode === 'svg') {
            propertyIcon = svgElement;
        } else {
            //Default 'web' version
            propertyIcon = document.createElement('i');
            propertyIcon.className = 'cfm-layer-entry-icon';

            propertyIcon.appendChild(svgElement);
        }


        if(options && options.format === 'outerHTML') {
            propertyIcon = propertyIcon.outerHTML;
        }

        if (callback) {
            callback(propertyIcon);
        }

        return propertyIcon;
    }
}